import React, { useEffect, useState } from 'react';
import { Tabs } from '@amzn/awsui-components-react/polaris';
import ServiceCollection from 'src/services/ServiceCollection';
import TablesView from 'src/components/custom-tables/TablesView';
import SegmentTablesView from 'src/components/segment-cdt/list/SegmentTablesView';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { useLocation } from 'react-router-dom';
import CONSTANTS from 'src/utils/constants';

/**
 * Host for the Custom Data Tables page. When `segmentCdtEnabled` is on it splits into
 * "CLI Data Tables" (the existing view) and "Segment Data Tables" (new) tabs. When the flag is off
 * or still resolving it renders the CLI view directly with no tab chrome — so the established screen
 * is byte-for-byte unchanged while the segment feature ships dark.
 */
export default function CustomDataTablesPage(props: { services: ServiceCollection }) {
    const { services } = props;
    const [segmentEnabled, setSegmentEnabled] = useState<boolean | null>(null);
    // Default to the CLI tab: enabling segmentCdtEnabled must not drop existing users onto the Segment tab,
    // and it defers the Segment list fetch until that tab is opened. `?tab=segment` is the one exception,
    // used by the "Segment Data Tables" breadcrumb on a segment table's detail page to land back on its list.
    const { search } = useLocation();
    const [activeTabId, setActiveTabId] = useState(() =>
        new URLSearchParams(search).get('tab') === CONSTANTS.PAGE_NAV.CUSTOM_TABLES.SEGMENT_TABLES_TAB_ID
            ? CONSTANTS.PAGE_NAV.CUSTOM_TABLES.SEGMENT_TABLES_TAB_ID
            : 'cli');

    useEffect(() => {
        let active = true;
        getFeatureFlags().then(flags => { if (active) { setSegmentEnabled(flags.segmentCdtEnabled); } });
        return () => { active = false; };
    }, []);

    if (!segmentEnabled) {
        return <TablesView services={services} />;
    }

    return (
        <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
            tabs={[
                { id: 'cli', label: 'CLI Data Tables', content: <TablesView services={services} /> },
                {
                    id: CONSTANTS.PAGE_NAV.CUSTOM_TABLES.SEGMENT_TABLES_TAB_ID,
                    label: CONSTANTS.PAGE_NAV.CUSTOM_TABLES.SEGMENT_TABLES_TAB_TITLE,
                    content: <SegmentTablesView services={services} />,
                },
            ]}
        />
    );
}
